// Phase 17a — i18n dictionaries + supported-language matrix.
//
// Contract:
//   • All translation lookups go through `t(key)` in I18nContext.
//   • Missing keys fall back to English silently (with a console.warn).
//   • The `asl` locale is INTENTIONALLY the empty object — it inherits
//     English text; the "asl" flag is a CSS/accessibility mode switch
//     (high contrast, motion-reduced, larger tap targets), NOT a language
//     translation. See a11y-asl.css.
//
// Keep this file the single source of truth for user-facing strings. Do
// NOT scatter translatable strings across components.

export const SUPPORTED_LANGUAGES = ["en", "es", "it", "asl"];

// Phase 18 — canonical DISPLAY ORDER (signup select + avatar sub-menu).
// Kept separate from SUPPORTED_LANGUAGES so the fallback chain (which
// depends on the historical order) is untouched. UI callers MUST use
// this constant.
export const SUPPORTED_LANGUAGES_ORDERED = ["es", "en", "it", "asl"];

export const LANGUAGE_LABELS = {
  en: "English",
  es: "Español",
  it: "Italiano",
  asl: "ASL (Sign Language Mode)",
};

const en = {
  // Navigation
  "nav.inventory": "Inventory",
  "nav.radar": "Radar",
  "nav.chat": "Chat",
  "nav.share_earn": "Share & Earn",
  "nav.market": "Market",

  // Auth
  "auth.login_title": "Sign In",
  "auth.signup_title": "Create Account",
  "auth.email_label": "Email",
  "auth.password_label": "Password",
  "auth.confirm_password_label": "Confirm password",
  "auth.language_label": "Preferred language",
  "auth.language_help":
    "Select ASL for high-contrast, motion-reduced accessibility mode.",
  "auth.marketing_consent":
    "I agree to the Terms & Privacy Policy and consent to receive promotional emails and updates.",
  "auth.submit_login": "Sign In",
  "auth.submit_signup": "Create Account",
  "auth.switch_to_signup": "Don't have an account? Sign up",
  "auth.switch_to_login": "Already have an account? Sign in",
  "auth.error_generic": "Something went wrong. Please try again.",
  "auth.error_invalid_credentials": "Invalid email or password.",
  // Phase 20 — Adult verification (18+)
  "auth.dob_label": "Date of Birth",
  "auth.dob_placeholder": "Select your date of birth",
  "auth.dob_error_underage":
    "Access Denied. FiguMatch is strictly for users aged 18 and older. If you are a parent helping your child, the account must be created using the parent's verified details.",
  "auth.legal_18_checkbox":
    "I certify that I am at least 18 years old. I understand that FiguMatch is strictly a digital matchmaking database for adults and holds zero liability for independent physical interactions. I agree to the Terms of Service and Privacy Policy.",
  "auth.adult_ack_required":
    "You must acknowledge the adult terms to continue.",

  // Phase 21 — FiguScan (client-side OCR + grade picker)
  "figuscan.button_label": "Scan Sticker",
  "figuscan.modal_title": "Scan a Sticker",
  "figuscan.frame_instruction":
    "Frame the sticker with its number visible in the corner",
  "figuscan.upload_fallback":
    "Camera unavailable. Upload a photo instead.",
  "figuscan.analyzing": "Analyzing…",
  "figuscan.error_no_number":
    "Couldn't read the number clearly. Try better lighting or enter manually.",
  "figuscan.number_label": "Sticker Number (1–650)",
  "figuscan.grade_label": "Condition Grade",
  "figuscan.target_duplicate": "Add to Duplicates",
  "figuscan.target_missing": "Add to Missing",
  "figuscan.confirm_button": "Add to Inventory",
  "figuscan.privacy_note":
    "Your image stays on your device. Only the extracted number is saved.",
  "figuscan.cancel": "Cancel",
  "figuscan.capture": "Capture",
  "figuscan.retake": "Retake",
  "figuscan.already_added":
    "Sticker #{n} is already in your {list}.",
  "figuscan.grade_M_label": "Mint — trade-ready",
  "figuscan.grade_NM_label": "Near Mint — solid condition",
  "figuscan.grade_EX_label": "Excellent — light handling",
  "figuscan.grade_G_label": "Good — visible wear",
  "figuscan.grade_F_label": "Fair — honest use",
  "figuscan.grade_HW_label": "Heavy Wear — trade with disclosure",
  "figuscan.tonality_M":
    "Excellent! This sticker is trade-ready. Add it to your inventory?",
  "figuscan.tonality_NM": "Solid condition. Great for trades.",
  "figuscan.tonality_EX":
    "Light handling shows. Trade with normal expectations.",
  "figuscan.tonality_G":
    "Visible wear. Still trade-eligible with disclosure to your partner.",
  "figuscan.tonality_F":
    "Shows honest use. Be transparent with your trade partner.",
  "figuscan.tonality_HW":
    "Significant condition issues. Only trade with clear, upfront disclosure.",
  "figuscan.tonality_verify_needed":
    "I couldn't confirm this sticker. Please verify manually before adding, or skip and try again.",
  "figuscan.tonality_scan_error":
    "Couldn't read the number clearly. Try better lighting or enter manually.",

  // Phase 22a — MatchMarket (dual-tab shell + fixed-price listings)
  "market.subtitle":
    "Buy and sell duplicates with nearby collectors — FiguTokens only.",
  "market.tab_matchmarket": "MatchMarket",
  "market.tab_matchbid": "MatchBid",
  "market.matchbid_coming_soon": "Auctions are coming soon. Watch this space!",
  "market.drop_here_to_list": "Drop here to list on MatchMarket",
  "market.drop_help":
    "Drag any duplicate sticker chip here — or create one manually.",
  "market.create_manual": "Create listing manually",
  "market.empty_no_listings": "No listings within 5km yet. Be the first to sell.",
  "market.create_title": "Create Listing",
  "market.number_label": "Sticker Number (1–650)",
  "market.grade_label": "Condition Grade",
  "market.price_label": "Price (FiguTokens)",
  "market.duration_label": "Duration",
  "market.days": "days",
  "market.description_label": "Description (optional)",
  "market.description_placeholder": "Any handling notes for the buyer?",
  "market.photos_label": "Photos (optional, max 5)",
  "market.add_photo": "Add photo",
  "market.photo_too_large": "Photo exceeds 5MB — pick a smaller file.",
  "market.photo_upload_unavailable":
    "Photo uploads are unavailable right now. Text-only listings still work.",
  "market.not_a_duplicate":
    "You can only list stickers already in your Duplicates.",
  "market.cancel": "Cancel",
  "market.submit": "Create listing",
  "market.listing_created": "Your listing is live.",
  "market.buy_for": "Buy for {price} FT",
  "market.cancel_listing": "Cancel listing",
  "market.cancel_success": "Listing cancelled.",
  "market.seller_label": "Seller",
  "market.meetup_pre_purchase": "Meetup coordinated via Safe Zone after purchase.",
  "market.suggested_safe_zones": "Suggested Safe Zones",
  "market.purchase_success": "Purchase confirmed.",
  "market.purchase_confirmed_title": "Purchase confirmed",
  "market.purchase_confirmed_body":
    "You paid {price} FiguTokens. Coordinate meetup via chat and the suggested Safe Zone.",
  "market.open_chat_cta": "Open chat with seller",
  "market.err_insufficient": "Not enough FiguTokens — top up via the Wallet.",
  "market.err_already_sold": "This listing is no longer available.",
  "market.err_self_purchase": "You cannot buy your own listing.",
  "market.status_sold": "Sold.",
  "market.status_cancelled": "Cancelled.",
  "market.status_expired": "Expired.",
  "market.not_found": "Listing not available.",
  "market.loading": "Loading…",

  // Phase 22b — MatchBid (auctions)
  "matchbid.list_header": "Live auctions within 5km — updated every 3 seconds.",
  "matchbid.create_button": "Start an auction",
  "matchbid.create_title": "Start Auction",
  "matchbid.empty": "No live auctions within 5km. Start one yourself.",
  "matchbid.mode_fixed": "Fixed price",
  "matchbid.mode_auction": "Auction",
  "matchbid.starting_price_label": "Starting price (FT)",
  "matchbid.reserve_price_label": "Reserve (optional)",
  "matchbid.reserve_placeholder": "no reserve",
  "matchbid.min_increment_label": "Min increment",
  "matchbid.duration_label": "Auction length",
  "matchbid.duration_1h": "1 hour",
  "matchbid.duration_6h": "6 hours",
  "matchbid.duration_1d": "1 day",
  "matchbid.duration_3d": "3 days",
  "matchbid.duration_7d": "7 days",
  "matchbid.err_reserve_below_starting":
    "Reserve must be at least the starting price.",
  "matchbid.submit": "Start auction",
  "matchbid.created_toast": "Your auction is live.",
  "matchbid.current_bid": "current bid",
  "matchbid.starting_at": "starting at",
  "matchbid.bids_short": "bids",
  "matchbid.time_left": "time left",
  "matchbid.grade_label": "Grade",
  "matchbid.seller_label": "Seller",
  "matchbid.no_bids_yet": "No bids yet — be the first!",
  "matchbid.min_next_bid":
    "Min next bid: {min} FT · Your balance: {bal} FT",
  "matchbid.bid_placeholder": "min {min} FT",
  "matchbid.place_bid_button": "Place bid",
  "matchbid.bid_success": "Bid placed. You're on top for now.",
  "matchbid.outbid_toast":
    "You've been outbid! Current bid: {price} FT.",
  "matchbid.extended_toast":
    "Auction extended by 30s — someone bid at the buzzer.",
  "matchbid.extended_own_toast":
    "Your bid extended the auction by 30s.",
  "matchbid.err_insufficient":
    "Not enough FiguTokens — top up via the Wallet.",
  "matchbid.err_bid_too_low":
    "Your bid is below the required minimum ({required} FT).",
  "matchbid.err_outbid":
    "Someone bid faster or the auction just ended. Try again.",
  "matchbid.err_own_auction": "You cannot bid on your own auction.",
  "matchbid.err_not_active": "This auction is no longer accepting bids.",
  "matchbid.err_cannot_cancel_with_bids":
    "Auctions with bids must run to close.",
  "matchbid.reserve_met": "Reserve met — will sell.",
  "matchbid.reserve_not_met_yet": "Reserve not met yet (hidden amount).",
  "matchbid.you_are_highest": "You're the top bidder.",
  "matchbid.you_label": "You",
  "matchbid.bid_history_title": "Recent bids",
  "matchbid.cancel_auction": "Cancel auction",
  "matchbid.cancel_success": "Auction cancelled.",
  "matchbid.status_won": "You won this auction!",
  "matchbid.status_sold": "Auction ended.",
  "matchbid.status_reserve_not_met":
    "Reserve price not met — no sale.",
  "matchbid.status_expired_no_bids":
    "Auction expired with no bids.",
  "matchbid.status_cancelled": "Cancelled by seller.",
  "matchbid.loading": "Loading…",
  "matchbid.not_found": "Auction not available.",
  "inventory.duplicates_title": "My Duplicates",
  "inventory.missing_title": "My Missing",
  "inventory.stat_duplicates": "DUPLICATES",
  "inventory.stat_missing": "MISSING",
  "inventory.stat_progress": "ALBUM PROGRESS",
  "inventory.stat_streak": "STREAK",
  "inventory.streak_days": "days",
  "inventory.empty_hint":
    "Tap a number to add it to your duplicates or missing list.",

  // Radar
  "radar.title": "Radar",
  "radar.subtitle": "Collectors within 5km with mutual trades.",
  "radar.no_matches": "No matches nearby yet. Invite friends to grow the map.",
  "radar.request_location": "Enable location to scan the 5km radius.",

  // Chat
  "chat.title": "Chat",
  "chat.empty": "No conversations yet. Head to Radar to find a match.",
  "chat.input_placeholder": "Type a message…",
  "chat.send": "Send",
  "chat.safety_banner":
    "Meet at an official FiguMatch Safe Zone. Never share your home address.",

  // Share
  "share.title": "Share & Earn",
  "share.subtitle": "Invite a collector, earn 10 FiguTokens.",
  "share.copy_link": "Copy invite link",
  "share.copied": "Link copied",

  // Alerts / toasts
  "alerts.language_updated": "Language updated.",
  "alerts.language_update_failed":
    "Could not update language. Please try again.",
  "alerts.generic_error": "Something went wrong.",
  "alerts.saving": "Saving…",

  // Common buttons
  "buttons.save": "Save",
  "buttons.cancel": "Cancel",
  "buttons.close": "Close",
  "buttons.retry": "Retry",
  "buttons.confirm": "Confirm",

  // Trade — Phase 17b Silent-Match QR Handshake
  "trade.silent_trade": "Silent Trade",
  "trade.scan_qr": "Scan Trade QR",
  "trade.generate_title": "Silent Trade",
  "trade.generate_subtitle": "Show this QR to your counterpart.",
  "trade.generate_subtitle_with_name": "Show this QR to {name}.",
  "trade.generating": "Preparing your trade code…",
  "trade.pin_label": "Or share this PIN",
  "trade.expires_in": "Expires in",
  "trade.expired": "Expired",
  "trade.waiting_for_scan": "Waiting for the other collector to scan…",
  "trade.you_give_short": "You give",
  "trade.you_get_short": "You get",
  "trade.scan_title": "Scan a Trade QR",
  "trade.scan_subtitle": "Point your camera at the QR, or type the PIN.",
  "trade.enter_pin": "Or enter the 6-digit PIN",
  "trade.pin_submit": "Match",
  "trade.camera_error": "Camera unavailable. Use the PIN below.",
  "trade.camera_unsupported": "This device does not support camera access. Use the PIN.",
  "trade.cancel": "Cancel trade",
  "trade.close": "Close",
  "trade.have_label": "YOU GIVE",
  "trade.want_label": "YOU GET",
  "trade.err.token_expired": "This code has expired. Ask your counterpart to generate a new one.",
  "trade.err.token_invalid": "Invalid trade code.",
  "trade.err.token_wrong_kind": "That QR is not a trade code.",
  "trade.err.pin_not_found_or_expired": "PIN not found or expired.",
  "trade.err.not_the_intended_target": "This trade is for another collector.",
  "trade.err.already_matched": "This trade was already completed.",
  "trade.err.cannot_scan_own_qr": "You can't scan your own QR.",
  "trade.err.target_not_in_radar": "This collector is out of range (5 km).",
  "trade.err.no_mutual_trade": "No mutual trade with this collector yet.",
  "trade.err.cannot_trade_with_self": "You can't trade with yourself.",
  "trade.err.generic": "Something went wrong. Try again.",

  // Phase 18 — UX polish
  "app.language_menu_label": "Language",
  "trade.scan_camera_disclaimer":
    "Camera access is strictly used to scan your teammate's QR code.",
  "trade.tab_camera": "Camera Scanner",
  "trade.tab_pin": "Manual PIN Input",
  "trade.camera_denied_message": "Camera unavailable. Use the PIN below.",
  "trade.pairing_pin_label": "YOUR PAIRING PIN",
  "trade.error_modal_title": "Invalid or Expired Match Data",

  // Phase 19b — FiguTour i18n + step 6 (wallet)
  "figutour.step1_welcome":
    "Welcome, Collector. I am Robo-Ball. Let's begin the tour through your trading empire.",
  "figutour.step2_inventory":
    "Here lives your collection. Tap numbers to register duplicates and missing stickers.",
  "figutour.step3_radar":
    "The Radar scans collectors within 5km. Find mutual matches and trade instantly.",
  "figutour.step4_rewards":
    "The Share & Earn section is your fuel. Invite friends to secure your first 10 FiguTokens.",
  "figutour.step5_launch": "You're ready! Start trading like a pro.",
  "figutour.step6_wallet":
    "One more thing! Every friend who signs up with your link earns you 10 FiguTokens straight to your FiguWallet. Spend them on the Radar's Quick Boost Emergency for 3 days and sweep up the stickers you're missing.",
  "figutour.skip_tour": "Skip tour",
  "figutour.step_progress": "STEP {current} OF {total}",

  // Phase 19b — FiguWallet
  "wallet.aria_label": "Open FiguMatch Premium Shop",
  "wallet.figutokens_label": "FiguTokens",

  // Phase 19b — Premium Shop
  "shop.title": "FiguMatch Premium Shop",
  "shop.subtitle": "Fuel your trades.",
  "shop.balance_label": "Balance",
  "shop.buy": "Buy",
  "shop.activate": "Activate",
  "shop.subscribe": "Subscribe",
  "shop.coming_soon": "Coming soon",
  "shop.checkout_error": "Checkout failed. Try again.",
  "shop.token_bundles_title": "FiguToken Bundles",
  "shop.boosters_title": "Boosters",
  "shop.subscriptions_title": "Subscriptions",
  "shop.booster_activated": "Booster activated!",
  "shop.insufficient_balance": "Not enough FiguTokens. Grab a bundle above.",
  "shop.insufficient_balance_short": "Need more",
  "shop.booster_already_active": "You already have an active booster.",
  "shop.booster_active_until": "Active until {date}",
  "shop.proximity_lightning_name": "Proximity Lightning",
  "shop.proximity_lightning_desc": "15km Radar for 1 day",
  "shop.streak_minibus_name": "Racha Mini-Bus",
  "shop.streak_minibus_desc": "Featured profile for 3 days",
  "shop.quick_boost_3d_name": "Quick Boost Emergency",
  "shop.quick_boost_3d_desc": "Featured profile for 3 days",
  "shop.pro_name": "PRO",
  "shop.sub_pro_name": "PRO",
  "shop.pro_features": "Priority Radar refresh · Custom jersey · Ad-free",
  "shop.sub_pro_features": "Priority Radar refresh · Custom jersey · Ad-free",
  "shop.master_name": "Master Collector",
  "shop.sub_master_name": "Master Collector",
  "shop.master_features":
    "Everything in PRO · 30km Radar · Free Racha Mini-Bus monthly",
  "shop.sub_master_features":
    "Everything in PRO · 30km Radar · Free Racha Mini-Bus monthly",
  "shop.tokens_500_name": "Starter Bundle",
  "shop.tokens_1000_name": "Trader Bundle",
  "shop.tokens_1500_name": "Champion Bundle",

  // Phase 19c/d — Robo-Ball periodic greeting (fires every 60 s in
  // non-ASL mode; degrades to a static single-shot badge in ASL mode).
  "roboball.periodic_greeting": "I'm here to help with your trade!",
  "roboball.greeting_dismiss": "Dismiss",

  // Phase 19c/d — Admin dashboard
  "admin.nav_item": "Admin Dashboard",
  "admin.unlock_title": "Admin Unlock",
  "admin.unlock_subtitle":
    "Enter the shared admin secret to access the telemetry dashboard.",
  "admin.unlock_placeholder": "Admin Secret Key",
  "admin.unlock_button": "Unlock",
  "admin.unlock_denied": "Access Denied. Exclusive Admin Zone.",
  "admin.lock_button": "Lock",
  "admin.dashboard_title": "FiguMatch — Command Center",
  "admin.card.market_funnel": "Market Funnel",
  "admin.card.token_circulation": "Token Circulation",
  "admin.card.live_snapshot": "Live Snapshot",
  "admin.card.brand_demand": "Brand Demand Index",
  "admin.card.legacy_telemetry": "Legacy Telemetry (Phase 14)",
  "admin.kpi.total_signups": "Total Signups",
  "admin.kpi.total_tokens": "FiguTokens in circulation",
  "admin.kpi.stripe_events": "Stripe events processed",
  "admin.kpi.active_sessions": "Active sessions (24h)",
  "admin.kpi.pwa_installs": "PWA installations",
  "admin.kpi.top_numbers": "Top-20 numbers by matched-trade volume",
  "admin.ready_for_commercialization": "Ready for commercialization",
};

const es = {
  // Nav
  "nav.inventory": "Inventario",
  "nav.radar": "Radar",
  "nav.chat": "Chat",
  "nav.share_earn": "Compartir y Ganar",
  "nav.market": "Mercado",

  // Auth
  "auth.login_title": "Iniciar sesión",
  "auth.signup_title": "Crear cuenta",
  "auth.email_label": "Correo electrónico",
  "auth.password_label": "Contraseña",
  "auth.confirm_password_label": "Confirmar contraseña",
  "auth.language_label": "Idioma preferido",
  "auth.language_help":
    "Selecciona ASL para el modo de accesibilidad con alto contraste y movimiento reducido.",
  "auth.marketing_consent":
    "Acepto los Términos y la Política de Privacidad, y consiento recibir correos promocionales y novedades.",
  "auth.submit_login": "Iniciar sesión",
  "auth.submit_signup": "Crear cuenta",
  "auth.switch_to_signup": "¿No tienes cuenta? Regístrate",
  "auth.switch_to_login": "¿Ya tienes cuenta? Inicia sesión",
  "auth.error_generic": "Algo salió mal. Vuelve a intentarlo.",
  "auth.error_invalid_credentials": "Correo o contraseña incorrectos.",
  // Phase 20 — Verificación de adulto (18+)
  "auth.dob_label": "Fecha de Nacimiento",
  "auth.dob_placeholder": "Selecciona tu fecha de nacimiento",
  "auth.dob_error_underage":
    "Acceso Denegado. FiguMatch es estrictamente para usuarios de 18 años o más. Si eres un padre ayudando a tu hijo, la cuenta debe crearse utilizando los datos verificados del padre.",
  "auth.legal_18_checkbox":
    "Certifico que tengo al menos 18 años de edad. Entiendo que FiguMatch es estrictamente una base de datos digital de emparejamiento para adultos y no tiene ninguna responsabilidad por las interacciones físicas independientes. Acepto los Términos de Servicio y la Política de Privacidad.",
  "auth.adult_ack_required":
    "Debes aceptar los términos para adultos para continuar.",

  // Phase 21 — FiguScan
  "figuscan.button_label": "Escanear Sticker",
  "figuscan.modal_title": "Escanear un Sticker",
  "figuscan.frame_instruction":
    "Encuadra el sticker con su número visible en la esquina",
  "figuscan.upload_fallback":
    "Cámara no disponible. Sube una foto en su lugar.",
  "figuscan.analyzing": "Analizando…",
  "figuscan.error_no_number":
    "No pude leer el número claramente. Prueba con mejor iluminación o ingresa manualmente.",
  "figuscan.number_label": "Número de Sticker (1–650)",
  "figuscan.grade_label": "Calificación de Condición",
  "figuscan.target_duplicate": "Agregar a Duplicados",
  "figuscan.target_missing": "Agregar a Faltantes",
  "figuscan.confirm_button": "Agregar al Inventario",
  "figuscan.privacy_note":
    "Tu imagen queda en tu dispositivo. Solo se guarda el número extraído.",
  "figuscan.cancel": "Cancelar",
  "figuscan.capture": "Capturar",
  "figuscan.retake": "Volver a tomar",
  "figuscan.already_added":
    "El sticker #{n} ya está en tus {list}.",
  "figuscan.grade_M_label": "Perfecta — lista para intercambio",
  "figuscan.grade_NM_label": "Casi Perfecta — buena condición",
  "figuscan.grade_EX_label": "Excelente — uso ligero",
  "figuscan.grade_G_label": "Buena — desgaste visible",
  "figuscan.grade_F_label": "Aceptable — uso honesto",
  "figuscan.grade_HW_label": "Desgastada — intercambiar con divulgación",
  "figuscan.tonality_M":
    "¡Excelente! Este sticker está listo para intercambio. ¿Agregarlo a tu inventario?",
  "figuscan.tonality_NM": "Buena condición. Excelente para intercambios.",
  "figuscan.tonality_EX":
    "Muestra uso ligero. Intercambio con expectativas normales.",
  "figuscan.tonality_G":
    "Desgaste visible. Aún intercambiable con divulgación a tu compañero.",
  "figuscan.tonality_F":
    "Muestra uso honesto. Sé transparente con tu compañero de intercambio.",
  "figuscan.tonality_HW":
    "Problemas significativos de condición. Intercambia solo con divulgación clara y anticipada.",
  "figuscan.tonality_verify_needed":
    "No pude confirmar este sticker. Verifica manualmente antes de agregar, o cancela y vuelve a intentar.",
  "figuscan.tonality_scan_error":
    "No pude leer el número claramente. Prueba con mejor iluminación o ingresa manualmente.",

  // Phase 22a — MatchMarket
  "market.subtitle":
    "Compra y vende duplicados con coleccionistas cercanos — solo FiguTokens.",
  "market.tab_matchmarket": "MatchMarket",
  "market.tab_matchbid": "MatchBid",
  "market.matchbid_coming_soon":
    "¡Las subastas llegan pronto. Atento a este espacio!",
  "market.drop_here_to_list": "Suelta aquí para publicar en MatchMarket",
  "market.drop_help":
    "Arrastra cualquier duplicado hasta aquí — o crea una publicación manualmente.",
  "market.create_manual": "Crear publicación manualmente",
  "market.empty_no_listings":
    "Aún no hay publicaciones a menos de 5km. Sé el primero en vender.",
  "market.create_title": "Crear Publicación",
  "market.number_label": "Número de Sticker (1–650)",
  "market.grade_label": "Calificación de Condición",
  "market.price_label": "Precio (FiguTokens)",
  "market.duration_label": "Duración",
  "market.days": "días",
  "market.description_label": "Descripción (opcional)",
  "market.description_placeholder":
    "¿Algún detalle de manejo para el comprador?",
  "market.photos_label": "Fotos (opcional, máx. 5)",
  "market.add_photo": "Agregar foto",
  "market.photo_too_large": "La foto supera 5 MB — elige un archivo más pequeño.",
  "market.photo_upload_unavailable":
    "Las subidas de fotos no están disponibles ahora. Las publicaciones de solo texto siguen funcionando.",
  "market.not_a_duplicate":
    "Solo puedes publicar stickers que ya tengas en tus Duplicados.",
  "market.cancel": "Cancelar",
  "market.submit": "Publicar",
  "market.listing_created": "Tu publicación está activa.",
  "market.buy_for": "Comprar por {price} FT",
  "market.cancel_listing": "Cancelar publicación",
  "market.cancel_success": "Publicación cancelada.",
  "market.seller_label": "Vendedor",
  "market.meetup_pre_purchase":
    "El encuentro se coordina en la Zona Segura tras la compra.",
  "market.suggested_safe_zones": "Zonas Seguras sugeridas",
  "market.purchase_success": "Compra confirmada.",
  "market.purchase_confirmed_title": "Compra confirmada",
  "market.purchase_confirmed_body":
    "Pagaste {price} FiguTokens. Coordina el encuentro por chat y en la Zona Segura sugerida.",
  "market.open_chat_cta": "Abrir chat con el vendedor",
  "market.err_insufficient": "FiguTokens insuficientes — recarga desde tu Wallet.",
  "market.err_already_sold": "Esta publicación ya no está disponible.",
  "market.err_self_purchase": "No puedes comprar tu propia publicación.",
  "market.status_sold": "Vendida.",
  "market.status_cancelled": "Cancelada.",
  "market.status_expired": "Expirada.",
  "market.not_found": "Publicación no disponible.",
  "market.loading": "Cargando…",

  // Phase 22b — MatchBid (subastas)
  "matchbid.list_header":
    "Subastas en vivo dentro de 5km — se actualiza cada 3 segundos.",
  "matchbid.create_button": "Iniciar una subasta",
  "matchbid.create_title": "Iniciar Subasta",
  "matchbid.empty": "No hay subastas en vivo dentro de 5km. Inicia una tú mismo.",
  "matchbid.mode_fixed": "Precio fijo",
  "matchbid.mode_auction": "Subasta",
  "matchbid.starting_price_label": "Precio inicial (FT)",
  "matchbid.reserve_price_label": "Reserva (opcional)",
  "matchbid.reserve_placeholder": "sin reserva",
  "matchbid.min_increment_label": "Incremento mínimo",
  "matchbid.duration_label": "Duración de la subasta",
  "matchbid.duration_1h": "1 hora",
  "matchbid.duration_6h": "6 horas",
  "matchbid.duration_1d": "1 día",
  "matchbid.duration_3d": "3 días",
  "matchbid.duration_7d": "7 días",
  "matchbid.err_reserve_below_starting":
    "La reserva debe ser al menos igual al precio inicial.",
  "matchbid.submit": "Iniciar subasta",
  "matchbid.created_toast": "Tu subasta está activa.",
  "matchbid.current_bid": "puja actual",
  "matchbid.starting_at": "desde",
  "matchbid.bids_short": "pujas",
  "matchbid.time_left": "tiempo restante",
  "matchbid.grade_label": "Calificación",
  "matchbid.seller_label": "Vendedor",
  "matchbid.no_bids_yet": "Sin pujas todavía — ¡sé el primero!",
  "matchbid.min_next_bid":
    "Puja mínima siguiente: {min} FT · Tu saldo: {bal} FT",
  "matchbid.bid_placeholder": "mín {min} FT",
  "matchbid.place_bid_button": "Hacer puja",
  "matchbid.bid_success": "Puja realizada. Vas primero por ahora.",
  "matchbid.outbid_toast":
    "¡Te han superado! Puja actual: {price} FT.",
  "matchbid.extended_toast":
    "Subasta extendida 30s — alguien pujó sobre la bocina.",
  "matchbid.extended_own_toast":
    "Tu puja extendió la subasta 30s.",
  "matchbid.err_insufficient":
    "FiguTokens insuficientes — recarga desde tu Wallet.",
  "matchbid.err_bid_too_low":
    "Tu puja está por debajo del mínimo requerido ({required} FT).",
  "matchbid.err_outbid":
    "Alguien pujó más rápido o la subasta acaba de terminar. Inténtalo de nuevo.",
  "matchbid.err_own_auction": "No puedes pujar en tu propia subasta.",
  "matchbid.err_not_active": "Esta subasta ya no acepta pujas.",
  "matchbid.err_cannot_cancel_with_bids":
    "Las subastas con pujas deben terminar su ciclo.",
  "matchbid.reserve_met": "Reserva alcanzada — se venderá.",
  "matchbid.reserve_not_met_yet":
    "Reserva no alcanzada aún (cantidad oculta).",
  "matchbid.you_are_highest": "Eres el mejor postor.",
  "matchbid.you_label": "Tú",
  "matchbid.bid_history_title": "Pujas recientes",
  "matchbid.cancel_auction": "Cancelar subasta",
  "matchbid.cancel_success": "Subasta cancelada.",
  "matchbid.status_won": "¡Ganaste esta subasta!",
  "matchbid.status_sold": "Subasta finalizada.",
  "matchbid.status_reserve_not_met":
    "Precio de reserva no alcanzado — sin venta.",
  "matchbid.status_expired_no_bids":
    "La subasta expiró sin pujas.",
  "matchbid.status_cancelled": "Cancelada por el vendedor.",
  "matchbid.loading": "Cargando…",
  "matchbid.not_found": "Subasta no disponible.",
  "inventory.duplicates_title": "Mis Duplicados",
  "inventory.missing_title": "Mis Faltantes",
  "inventory.stat_duplicates": "DUPLICADOS",
  "inventory.stat_missing": "FALTANTES",
  "inventory.stat_progress": "PROGRESO DEL ÁLBUM",
  "inventory.stat_streak": "RACHA",
  "inventory.streak_days": "días",
  "inventory.empty_hint":
    "Toca un número para añadirlo a tus duplicados o faltantes.",

  // Radar
  "radar.title": "Radar",
  "radar.subtitle": "Coleccionistas a menos de 5 km con intercambios mutuos.",
  "radar.no_matches":
    "Aún no hay coincidencias cerca. Invita a amigos para hacer crecer el mapa.",
  "radar.request_location":
    "Activa la ubicación para escanear el radio de 5 km.",

  // Chat
  "chat.title": "Chat",
  "chat.empty":
    "Aún no hay conversaciones. Ve al Radar para encontrar una coincidencia.",
  "chat.input_placeholder": "Escribe un mensaje…",
  "chat.send": "Enviar",
  "chat.safety_banner":
    "Reúnete en una Zona Segura oficial de FiguMatch. Nunca compartas tu dirección.",

  // Share
  "share.title": "Compartir y Ganar",
  "share.subtitle": "Invita a un coleccionista y gana 10 FiguTokens.",
  "share.copy_link": "Copiar enlace de invitación",
  "share.copied": "Enlace copiado",

  // Alerts
  "alerts.language_updated": "Idioma actualizado.",
  "alerts.language_update_failed":
    "No se pudo actualizar el idioma. Inténtalo de nuevo.",
  "alerts.generic_error": "Algo salió mal.",
  "alerts.saving": "Guardando…",

  // Buttons
  "buttons.save": "Guardar",
  "buttons.cancel": "Cancelar",
  "buttons.close": "Cerrar",
  "buttons.retry": "Reintentar",
  "buttons.confirm": "Confirmar",

  // Trade — Fase 17b
  "trade.silent_trade": "Trueque Silencioso",
  "trade.scan_qr": "Escanear QR",
  "trade.generate_title": "Trueque Silencioso",
  "trade.generate_subtitle": "Muestra este QR a tu contraparte.",
  "trade.generate_subtitle_with_name": "Muestra este QR a {name}.",
  "trade.generating": "Preparando tu código de intercambio…",
  "trade.pin_label": "O comparte este PIN",
  "trade.expires_in": "Expira en",
  "trade.expired": "Expirado",
  "trade.waiting_for_scan": "Esperando a que el otro coleccionista escanee…",
  "trade.you_give_short": "Tú das",
  "trade.you_get_short": "Tú recibes",
  "trade.scan_title": "Escanea un QR de intercambio",
  "trade.scan_subtitle": "Apunta tu cámara al QR, o ingresa el PIN.",
  "trade.enter_pin": "O ingresa el PIN de 6 dígitos",
  "trade.pin_submit": "Coincidir",
  "trade.camera_error": "Cámara no disponible. Usa el PIN debajo.",
  "trade.camera_unsupported": "Este dispositivo no admite el acceso a la cámara. Usa el PIN.",
  "trade.cancel": "Cancelar intercambio",
  "trade.close": "Cerrar",
  "trade.have_label": "TÚ DAS",
  "trade.want_label": "TÚ RECIBES",
  "trade.err.token_expired": "Este código ha expirado. Pide a tu contraparte que genere uno nuevo.",
  "trade.err.token_invalid": "Código de intercambio no válido.",
  "trade.err.token_wrong_kind": "Ese QR no es un código de intercambio.",
  "trade.err.pin_not_found_or_expired": "PIN no encontrado o expirado.",
  "trade.err.not_the_intended_target": "Este intercambio es para otro coleccionista.",
  "trade.err.already_matched": "Este intercambio ya se completó.",
  "trade.err.cannot_scan_own_qr": "No puedes escanear tu propio QR.",
  "trade.err.target_not_in_radar": "Este coleccionista está fuera de rango (5 km).",
  "trade.err.no_mutual_trade": "Aún no hay intercambio mutuo con este coleccionista.",
  "trade.err.cannot_trade_with_self": "No puedes intercambiar contigo mismo.",
  "trade.err.generic": "Algo salió mal. Inténtalo de nuevo.",

  // Fase 18 — UX polish
  "app.language_menu_label": "Idioma",
  "trade.scan_camera_disclaimer":
    "El acceso a la cámara se usa exclusivamente para escanear el QR de tu compañero.",
  "trade.tab_camera": "Escanear Cámara",
  "trade.tab_pin": "Ingresar PIN",
  "trade.camera_denied_message": "Cámara no disponible. Usa el PIN debajo.",
  "trade.pairing_pin_label": "TU PIN DE EMPAREJAMIENTO",
  "trade.error_modal_title": "Datos de Emparejamiento Inválidos o Expirados",

  // Fase 19b — FiguTour i18n + paso 6 (wallet)
  "figutour.step1_welcome":
    "Bienvenido, Coleccionista. Soy Robo-Ball. Comencemos el recorrido por tu imperio de trueques.",
  "figutour.step2_inventory":
    "Aquí vive tu colección. Toca los números para registrar tus repetidas y las que te faltan.",
  "figutour.step3_radar":
    "El Radar escanea coleccionistas en 5 km. Encuentra coincidencias mutuas y trueca al instante.",
  "figutour.step4_rewards":
    "La sección Comparte y Gana es tu combustible. Invita amigos y asegura tus primeros 10 FiguTokens.",
  "figutour.step5_launch": "¡Estás listo! Empieza a truequear como un profesional.",
  "figutour.step6_wallet":
    "¡Y eso no es todo! Cada amigo que se registre con tu enlace te otorgará 10 FiguTokens directo a tu FiguWallet. Úsalos en el Radar para activar el Quick Boost de Emergencia por 3 días y barrer con las figuritas que te faltan.",
  "figutour.skip_tour": "Saltar tour",
  "figutour.step_progress": "PASO {current} DE {total}",

  // Fase 19b — FiguWallet
  "wallet.aria_label": "Abrir la Tienda Premium FiguMatch",
  "wallet.figutokens_label": "FiguTokens",

  // Fase 19b — Tienda Premium
  "shop.title": "Tienda Premium FiguMatch",
  "shop.subtitle": "Combustible para tus trueques.",
  "shop.balance_label": "Saldo",
  "shop.buy": "Comprar",
  "shop.activate": "Activar",
  "shop.subscribe": "Suscribirme",
  "shop.coming_soon": "Muy pronto",
  "shop.checkout_error": "Falló el pago. Inténtalo de nuevo.",
  "shop.token_bundles_title": "Paquetes de FiguTokens",
  "shop.boosters_title": "Boosters",
  "shop.subscriptions_title": "Suscripciones",
  "shop.booster_activated": "¡Booster activado!",
  "shop.insufficient_balance":
    "FiguTokens insuficientes. Consigue un paquete arriba.",
  "shop.insufficient_balance_short": "Faltan",
  "shop.booster_already_active": "Ya tienes un booster activo.",
  "shop.booster_active_until": "Activo hasta {date}",
  "shop.proximity_lightning_name": "Rayo de Proximidad",
  "shop.proximity_lightning_desc": "Radar de 15 km durante 1 día",
  "shop.streak_minibus_name": "Racha Mini-Bus",
  "shop.streak_minibus_desc": "Perfil destacado durante 3 días",
  "shop.quick_boost_3d_name": "Quick Boost de Emergencia",
  "shop.quick_boost_3d_desc": "Perfil destacado durante 3 días",
  "shop.pro_name": "PRO",
  "shop.pro_features":
    "Radar prioritario · Camiseta personalizada · Sin anuncios",
  "shop.master_name": "Coleccionista Maestro",
  "shop.master_features":
    "Todo de PRO · Radar 30 km · Racha Mini-Bus gratis al mes",
  "shop.tokens_500_name": "Paquete Inicial",
  "shop.tokens_1000_name": "Paquete Trader",
  "shop.tokens_1500_name": "Paquete Campeón",

  // Fase 19c/d — Saludo periódico de Robo-Ball (verbatim ES del brief)
  "roboball.periodic_greeting": "¡Estoy aquí para ayudarte con tu intercambio!",
  "roboball.greeting_dismiss": "Cerrar",

  // Fase 19c/d — Panel de administración
  "admin.nav_item": "Panel de Administración",
  "admin.unlock_title": "Acceso Administrativo",
  "admin.unlock_subtitle":
    "Ingresa la clave secreta de administrador para acceder al panel de telemetría.",
  "admin.unlock_placeholder": "Clave secreta de administrador",
  "admin.unlock_button": "Desbloquear",
  "admin.unlock_denied": "Acceso Denegado. Zona Exclusiva de Administradores.",
  "admin.lock_button": "Bloquear",
  "admin.dashboard_title": "FiguMatch — Centro de Comando",
  "admin.card.market_funnel": "Embudo de Mercado",
  "admin.card.token_circulation": "Circulación de Tokens",
  "admin.card.live_snapshot": "Instantánea en vivo",
  "admin.card.brand_demand": "Índice de Demanda de Marcas",
  "admin.card.legacy_telemetry": "Telemetría Heredada (Fase 14)",
  "admin.kpi.total_signups": "Registros totales",
  "admin.kpi.total_tokens": "FiguTokens en circulación",
  "admin.kpi.stripe_events": "Eventos de Stripe procesados",
  "admin.kpi.active_sessions": "Sesiones activas (24h)",
  "admin.kpi.pwa_installs": "Instalaciones PWA",
  "admin.kpi.top_numbers": "Top-20 números por volumen de intercambio",
  "admin.ready_for_commercialization": "Listo para comercialización",
};

const it = {
  // Nav
  "nav.inventory": "Inventario",
  "nav.radar": "Radar",
  "nav.chat": "Chat",
  "nav.share_earn": "Condividi e Guadagna",
  "nav.market": "Mercato",

  // Auth
  "auth.login_title": "Accedi",
  "auth.signup_title": "Crea account",
  "auth.email_label": "Email",
  "auth.password_label": "Password",
  "auth.confirm_password_label": "Conferma password",
  "auth.language_label": "Lingua preferita",
  "auth.language_help":
    "Seleziona ASL per la modalità di accessibilità ad alto contrasto e movimento ridotto.",
  "auth.marketing_consent":
    "Accetto i Termini e l'Informativa sulla Privacy e acconsento a ricevere email promozionali e aggiornamenti.",
  "auth.submit_login": "Accedi",
  "auth.submit_signup": "Crea account",
  "auth.switch_to_signup": "Non hai un account? Registrati",
  "auth.switch_to_login": "Hai già un account? Accedi",
  "auth.error_generic": "Qualcosa è andato storto. Riprova.",
  "auth.error_invalid_credentials": "Email o password non validi.",
  // Phase 20 — Verifica adulto (18+)
  "auth.dob_label": "Data di Nascita",
  "auth.dob_placeholder": "Seleziona la tua data di nascita",
  "auth.dob_error_underage":
    "Accesso Negato. FiguMatch è strettamente riservato agli utenti di età pari o superiore a 18 anni. Se sei un genitore che aiuta tuo figlio, l'account deve essere creato utilizzando i dati verificati del genitore.",
  "auth.legal_18_checkbox":
    "Certifico di avere almeno 18 anni. Comprendo che FiguMatch è strettamente un database digitale di abbinamento per adulti e non si assume alcuna responsabilità per le interazioni fisiche indipendenti. Accetto i Termini di Servizio e l'Informativa sulla Privacy.",
  "auth.adult_ack_required":
    "Devi accettare i termini per adulti per continuare.",

  // Phase 21 — FiguScan
  "figuscan.button_label": "Scansiona Figurina",
  "figuscan.modal_title": "Scansiona una Figurina",
  "figuscan.frame_instruction":
    "Inquadra la figurina con il suo numero visibile nell'angolo",
  "figuscan.upload_fallback":
    "Fotocamera non disponibile. Carica una foto invece.",
  "figuscan.analyzing": "Analisi in corso…",
  "figuscan.error_no_number":
    "Non ho letto il numero chiaramente. Prova con più luce o inseriscilo manualmente.",
  "figuscan.number_label": "Numero Figurina (1–650)",
  "figuscan.grade_label": "Valutazione Condizione",
  "figuscan.target_duplicate": "Aggiungi a Doppioni",
  "figuscan.target_missing": "Aggiungi a Mancanti",
  "figuscan.confirm_button": "Aggiungi all'Inventario",
  "figuscan.privacy_note":
    "La tua immagine resta sul dispositivo. Viene salvato solo il numero estratto.",
  "figuscan.cancel": "Annulla",
  "figuscan.capture": "Cattura",
  "figuscan.retake": "Rifai",
  "figuscan.already_added":
    "La figurina #{n} è già nei tuoi {list}.",
  "figuscan.grade_M_label": "Perfetta — pronta per lo scambio",
  "figuscan.grade_NM_label": "Quasi Perfetta — ottima condizione",
  "figuscan.grade_EX_label": "Eccellente — lieve uso",
  "figuscan.grade_G_label": "Buona — usura visibile",
  "figuscan.grade_F_label": "Discreta — uso onesto",
  "figuscan.grade_HW_label": "Molto Usurata — scambia con dichiarazione",
  "figuscan.tonality_M":
    "Ottimo! Questa figurina è pronta per lo scambio. Aggiungerla al tuo inventario?",
  "figuscan.tonality_NM": "Ottima condizione. Perfetta per gli scambi.",
  "figuscan.tonality_EX":
    "Mostra un lieve uso. Scambio con normali aspettative.",
  "figuscan.tonality_G":
    "Usura visibile. Ancora scambiabile con dichiarazione al tuo partner.",
  "figuscan.tonality_F":
    "Mostra uso onesto. Sii trasparente con il tuo partner di scambio.",
  "figuscan.tonality_HW":
    "Problemi significativi di condizione. Scambia solo con dichiarazione chiara e anticipata.",
  "figuscan.tonality_verify_needed":
    "Non ho potuto confermare questa figurina. Verifica manualmente prima di aggiungere, o annulla e riprova.",
  "figuscan.tonality_scan_error":
    "Non ho letto il numero chiaramente. Prova con più luce o inseriscilo manualmente.",

  // Phase 22a — MatchMarket
  "market.subtitle":
    "Compra e vendi doppioni con collezionisti vicini — solo FiguTokens.",
  "market.tab_matchmarket": "MatchMarket",
  "market.tab_matchbid": "MatchBid",
  "market.matchbid_coming_soon":
    "Le aste stanno arrivando. Resta sintonizzato!",
  "market.drop_here_to_list": "Rilascia qui per pubblicare su MatchMarket",
  "market.drop_help":
    "Trascina qui qualsiasi doppione — oppure crea manualmente.",
  "market.create_manual": "Crea inserzione manualmente",
  "market.empty_no_listings":
    "Nessuna inserzione entro 5km. Sii il primo a vendere.",
  "market.create_title": "Crea Inserzione",
  "market.number_label": "Numero Figurina (1–650)",
  "market.grade_label": "Valutazione Condizione",
  "market.price_label": "Prezzo (FiguTokens)",
  "market.duration_label": "Durata",
  "market.days": "giorni",
  "market.description_label": "Descrizione (opzionale)",
  "market.description_placeholder":
    "Note per il compratore sulla gestione?",
  "market.photos_label": "Foto (opzionale, max 5)",
  "market.add_photo": "Aggiungi foto",
  "market.photo_too_large": "La foto supera 5 MB — scegli un file più piccolo.",
  "market.photo_upload_unavailable":
    "Il caricamento delle foto non è disponibile ora. Le inserzioni testuali funzionano lo stesso.",
  "market.not_a_duplicate":
    "Puoi vendere solo figurine già presenti nei tuoi Doppioni.",
  "market.cancel": "Annulla",
  "market.submit": "Pubblica",
  "market.listing_created": "La tua inserzione è online.",
  "market.buy_for": "Compra per {price} FT",
  "market.cancel_listing": "Annulla inserzione",
  "market.cancel_success": "Inserzione annullata.",
  "market.seller_label": "Venditore",
  "market.meetup_pre_purchase":
    "L'incontro viene coordinato tramite Safe Zone dopo l'acquisto.",
  "market.suggested_safe_zones": "Safe Zone suggerite",
  "market.purchase_success": "Acquisto confermato.",
  "market.purchase_confirmed_title": "Acquisto confermato",
  "market.purchase_confirmed_body":
    "Hai pagato {price} FiguTokens. Coordina l'incontro in chat e nella Safe Zone suggerita.",
  "market.open_chat_cta": "Apri chat con il venditore",
  "market.err_insufficient":
    "FiguTokens insufficienti — ricarica dal Wallet.",
  "market.err_already_sold": "Questa inserzione non è più disponibile.",
  "market.err_self_purchase": "Non puoi comprare la tua stessa inserzione.",
  "market.status_sold": "Venduta.",
  "market.status_cancelled": "Annullata.",
  "market.status_expired": "Scaduta.",
  "market.not_found": "Inserzione non disponibile.",
  "market.loading": "Caricamento…",

  // Phase 22b — MatchBid (aste)
  "matchbid.list_header":
    "Aste dal vivo entro 5km — aggiornate ogni 3 secondi.",
  "matchbid.create_button": "Avvia un'asta",
  "matchbid.create_title": "Avvia Asta",
  "matchbid.empty": "Nessuna asta dal vivo entro 5km. Avviane una tu.",
  "matchbid.mode_fixed": "Prezzo fisso",
  "matchbid.mode_auction": "Asta",
  "matchbid.starting_price_label": "Prezzo iniziale (FT)",
  "matchbid.reserve_price_label": "Riserva (opzionale)",
  "matchbid.reserve_placeholder": "nessuna riserva",
  "matchbid.min_increment_label": "Incremento minimo",
  "matchbid.duration_label": "Durata dell'asta",
  "matchbid.duration_1h": "1 ora",
  "matchbid.duration_6h": "6 ore",
  "matchbid.duration_1d": "1 giorno",
  "matchbid.duration_3d": "3 giorni",
  "matchbid.duration_7d": "7 giorni",
  "matchbid.err_reserve_below_starting":
    "La riserva deve essere almeno pari al prezzo iniziale.",
  "matchbid.submit": "Avvia asta",
  "matchbid.created_toast": "La tua asta è online.",
  "matchbid.current_bid": "offerta attuale",
  "matchbid.starting_at": "da",
  "matchbid.bids_short": "offerte",
  "matchbid.time_left": "tempo rimanente",
  "matchbid.grade_label": "Valutazione",
  "matchbid.seller_label": "Venditore",
  "matchbid.no_bids_yet": "Ancora nessuna offerta — sii il primo!",
  "matchbid.min_next_bid":
    "Offerta minima successiva: {min} FT · Saldo: {bal} FT",
  "matchbid.bid_placeholder": "min {min} FT",
  "matchbid.place_bid_button": "Fai offerta",
  "matchbid.bid_success": "Offerta piazzata. Sei in testa per ora.",
  "matchbid.outbid_toast":
    "Sei stato superato! Offerta attuale: {price} FT.",
  "matchbid.extended_toast":
    "Asta prolungata di 30s — qualcuno ha offerto all'ultimo secondo.",
  "matchbid.extended_own_toast":
    "La tua offerta ha prolungato l'asta di 30s.",
  "matchbid.err_insufficient":
    "FiguTokens insufficienti — ricarica dal Wallet.",
  "matchbid.err_bid_too_low":
    "La tua offerta è sotto il minimo richiesto ({required} FT).",
  "matchbid.err_outbid":
    "Qualcuno ha offerto più in fretta o l'asta è appena finita. Riprova.",
  "matchbid.err_own_auction": "Non puoi offrire sulla tua stessa asta.",
  "matchbid.err_not_active": "Questa asta non accetta più offerte.",
  "matchbid.err_cannot_cancel_with_bids":
    "Le aste con offerte devono arrivare a scadenza.",
  "matchbid.reserve_met": "Riserva raggiunta — venderà.",
  "matchbid.reserve_not_met_yet":
    "Riserva non ancora raggiunta (importo nascosto).",
  "matchbid.you_are_highest": "Sei il miglior offerente.",
  "matchbid.you_label": "Tu",
  "matchbid.bid_history_title": "Offerte recenti",
  "matchbid.cancel_auction": "Annulla asta",
  "matchbid.cancel_success": "Asta annullata.",
  "matchbid.status_won": "Hai vinto questa asta!",
  "matchbid.status_sold": "Asta terminata.",
  "matchbid.status_reserve_not_met":
    "Prezzo di riserva non raggiunto — nessuna vendita.",
  "matchbid.status_expired_no_bids":
    "L'asta è scaduta senza offerte.",
  "matchbid.status_cancelled": "Annullata dal venditore.",
  "matchbid.loading": "Caricamento…",
  "matchbid.not_found": "Asta non disponibile.",
  "inventory.duplicates_title": "I Miei Doppioni",
  "inventory.missing_title": "I Miei Mancanti",
  "inventory.stat_duplicates": "DOPPIONI",
  "inventory.stat_missing": "MANCANTI",
  "inventory.stat_progress": "AVANZAMENTO ALBUM",
  "inventory.stat_streak": "STREAK",
  "inventory.streak_days": "giorni",
  "inventory.empty_hint":
    "Tocca un numero per aggiungerlo ai tuoi doppioni o mancanti.",

  // Radar
  "radar.title": "Radar",
  "radar.subtitle": "Collezionisti entro 5 km con scambi reciproci.",
  "radar.no_matches":
    "Nessuna corrispondenza nelle vicinanze. Invita amici per ampliare la mappa.",
  "radar.request_location":
    "Attiva la posizione per scansionare il raggio di 5 km.",

  // Chat
  "chat.title": "Chat",
  "chat.empty":
    "Ancora nessuna conversazione. Vai al Radar per trovare una corrispondenza.",
  "chat.input_placeholder": "Scrivi un messaggio…",
  "chat.send": "Invia",
  "chat.safety_banner":
    "Incontratevi in una Safe Zone ufficiale di FiguMatch. Non condividere mai il tuo indirizzo.",

  // Share
  "share.title": "Condividi e Guadagna",
  "share.subtitle": "Invita un collezionista, guadagna 10 FiguTokens.",
  "share.copy_link": "Copia link di invito",
  "share.copied": "Link copiato",

  // Alerts
  "alerts.language_updated": "Lingua aggiornata.",
  "alerts.language_update_failed":
    "Impossibile aggiornare la lingua. Riprova.",
  "alerts.generic_error": "Qualcosa è andato storto.",
  "alerts.saving": "Salvataggio…",

  // Buttons
  "buttons.save": "Salva",
  "buttons.cancel": "Annulla",
  "buttons.close": "Chiudi",
  "buttons.retry": "Riprova",
  "buttons.confirm": "Conferma",

  // Trade — Fase 17b
  "trade.silent_trade": "Baratto Silenzioso",
  "trade.scan_qr": "Scansiona QR",
  "trade.generate_title": "Baratto Silenzioso",
  "trade.generate_subtitle": "Mostra questo QR alla tua controparte.",
  "trade.generate_subtitle_with_name": "Mostra questo QR a {name}.",
  "trade.generating": "Preparazione del codice di scambio…",
  "trade.pin_label": "O condividi questo PIN",
  "trade.expires_in": "Scade tra",
  "trade.expired": "Scaduto",
  "trade.waiting_for_scan": "In attesa che l'altro collezionista scansioni…",
  "trade.you_give_short": "Tu dai",
  "trade.you_get_short": "Tu ricevi",
  "trade.scan_title": "Scansiona un QR di scambio",
  "trade.scan_subtitle": "Punta la fotocamera sul QR, o inserisci il PIN.",
  "trade.enter_pin": "O inserisci il PIN a 6 cifre",
  "trade.pin_submit": "Abbina",
  "trade.camera_error": "Fotocamera non disponibile. Usa il PIN qui sotto.",
  "trade.camera_unsupported": "Questo dispositivo non supporta l'accesso alla fotocamera. Usa il PIN.",
  "trade.cancel": "Annulla scambio",
  "trade.close": "Chiudi",
  "trade.have_label": "TU DAI",
  "trade.want_label": "TU RICEVI",
  "trade.err.token_expired": "Questo codice è scaduto. Chiedi alla tua controparte di generarne uno nuovo.",
  "trade.err.token_invalid": "Codice di scambio non valido.",
  "trade.err.token_wrong_kind": "Quel QR non è un codice di scambio.",
  "trade.err.pin_not_found_or_expired": "PIN non trovato o scaduto.",
  "trade.err.not_the_intended_target": "Questo scambio è per un altro collezionista.",
  "trade.err.already_matched": "Questo scambio è già stato completato.",
  "trade.err.cannot_scan_own_qr": "Non puoi scansionare il tuo QR.",
  "trade.err.target_not_in_radar": "Questo collezionista è fuori portata (5 km).",
  "trade.err.no_mutual_trade": "Nessuno scambio reciproco con questo collezionista.",
  "trade.err.cannot_trade_with_self": "Non puoi scambiare con te stesso.",
  "trade.err.generic": "Qualcosa è andato storto. Riprova.",

  // Fase 18 — UX polish
  "app.language_menu_label": "Lingua",
  "trade.scan_camera_disclaimer":
    "L'accesso alla fotocamera è usato esclusivamente per scansionare il QR del tuo compagno.",
  "trade.tab_camera": "Scansione Fotocamera",
  "trade.tab_pin": "Inserisci PIN",
  "trade.camera_denied_message": "Fotocamera non disponibile. Usa il PIN qui sotto.",
  "trade.pairing_pin_label": "IL TUO PIN DI ABBINAMENTO",
  "trade.error_modal_title": "Dati di Abbinamento Non Validi o Scaduti",

  // Fase 19b — FiguTour i18n + passo 6 (wallet)
  "figutour.step1_welcome":
    "Benvenuto, Collezionista. Sono Robo-Ball. Iniziamo il tour del tuo impero di scambi.",
  "figutour.step2_inventory":
    "Qui vive la tua collezione. Tocca i numeri per registrare doppie e figurine mancanti.",
  "figutour.step3_radar":
    "Il Radar cerca collezionisti entro 5 km. Trova corrispondenze reciproche e scambia all'istante.",
  "figutour.step4_rewards":
    "La sezione Condividi e Guadagna è il tuo carburante. Invita amici e assicurati i primi 10 FiguToken.",
  "figutour.step5_launch": "Sei pronto! Inizia a scambiare come un professionista.",
  "figutour.step6_wallet":
    "E non è tutto! Ogni amico che si iscrive con il tuo link ti regala 10 FiguToken direttamente sul tuo FiguWallet. Usali sul Radar per attivare il Quick Boost di Emergenza per 3 giorni e completare le figurine che ti mancano.",
  "figutour.skip_tour": "Salta il tour",
  "figutour.step_progress": "PASSO {current} DI {total}",

  // Fase 19b — FiguWallet
  "wallet.aria_label": "Apri il Negozio Premium FiguMatch",
  "wallet.figutokens_label": "FiguTokens",

  // Fase 19b — Negozio Premium
  "shop.title": "FiguMatch Negozio Premium",
  "shop.subtitle": "Alimenta i tuoi scambi.",
  "shop.balance_label": "Saldo",
  "shop.buy": "Acquista",
  "shop.activate": "Attiva",
  "shop.subscribe": "Abbonati",
  "shop.coming_soon": "In arrivo",
  "shop.checkout_error": "Pagamento fallito. Riprova.",
  "shop.token_bundles_title": "Pacchetti FiguToken",
  "shop.boosters_title": "Booster",
  "shop.subscriptions_title": "Abbonamenti",
  "shop.booster_activated": "Booster attivato!",
  "shop.insufficient_balance":
    "FiguToken insufficienti. Prendi un pacchetto qui sopra.",
  "shop.insufficient_balance_short": "Ne servono di più",
  "shop.booster_already_active": "Hai già un booster attivo.",
  "shop.booster_active_until": "Attivo fino al {date}",
  "shop.proximity_lightning_name": "Fulmine di Prossimità",
  "shop.proximity_lightning_desc": "Radar da 15 km per 1 giorno",
  "shop.streak_minibus_name": "Racha Mini-Bus",
  "shop.streak_minibus_desc": "Profilo in evidenza per 3 giorni",
  "shop.quick_boost_3d_name": "Quick Boost di Emergenza",
  "shop.quick_boost_3d_desc": "Profilo in evidenza per 3 giorni",
  "shop.pro_name": "PRO",
  "shop.pro_features":
    "Radar prioritario · Maglia personalizzata · Senza pubblicità",
  "shop.master_name": "Collezionista Maestro",
  "shop.master_features":
    "Tutto di PRO · Radar 30 km · Racha Mini-Bus gratis al mese",
  "shop.tokens_500_name": "Pacchetto Iniziale",
  "shop.tokens_1000_name": "Pacchetto Trader",
  "shop.tokens_1500_name": "Pacchetto Campione",

  // Fase 19c/d — Saluto periodico di Robo-Ball
  "roboball.periodic_greeting": "Sono qui per aiutarti con il tuo scambio!",
  "roboball.greeting_dismiss": "Chiudi",

  // Fase 19c/d — Pannello di amministrazione
  "admin.nav_item": "Pannello Admin",
  "admin.unlock_title": "Sblocco Admin",
  "admin.unlock_subtitle":
    "Inserisci la chiave segreta condivisa per accedere al pannello di telemetria.",
  "admin.unlock_placeholder": "Chiave segreta admin",
  "admin.unlock_button": "Sblocca",
  "admin.unlock_denied": "Accesso Negato. Zona Esclusiva Amministratori.",
  "admin.lock_button": "Blocca",
  "admin.dashboard_title": "FiguMatch — Centro di Comando",
  "admin.card.market_funnel": "Imbuto di Mercato",
  "admin.card.token_circulation": "Circolazione dei Token",
  "admin.card.live_snapshot": "Istantanea Live",
  "admin.card.brand_demand": "Indice della Domanda",
  "admin.card.legacy_telemetry": "Telemetria (Fase 14)",
  "admin.kpi.total_signups": "Iscrizioni totali",
  "admin.kpi.total_tokens": "FiguToken in circolazione",
  "admin.kpi.stripe_events": "Eventi Stripe elaborati",
  "admin.kpi.active_sessions": "Sessioni attive (24h)",
  "admin.kpi.pwa_installs": "Installazioni PWA",
  "admin.kpi.top_numbers": "Top-20 numeri per volume di scambio",
  "admin.ready_for_commercialization": "Pronto per la commercializzazione",
};

export const DICTIONARIES = {
  en,
  es,
  it,
  // ASL intentionally empty — inherits English via fallback. The `asl`
  // selection is a visual accessibility mode, not a translation.
  asl: {},
};
